import { Fragment, useMemo } from "react";
import { Chart, ChartTypes, formatNumber } from "@uicore";
import { Bar, TooltipProps } from "recharts";
import dayjs from "dayjs";
import { Stack } from "@altimateai/lego";
import { useAppState } from "@/modules/app/useAppContext";
import CostChartTooltip from "../../summary/components/CostChartTooltip";
import FailureComponent from "../../summary/components/FailureComponent";
import DatabricksCostChartSkeleton from "../summary/components/DatabricksCostChartSkeleton";
import {
  ESTIMATE_OPACITY,
  SUMMARY_TIME_SERIES_CHART_PROPS,
} from "../summary/components/costConstants";
import { CLOUD_COST_CATEGORIES, CLOUD_TOTAL_KEY, cloudCostColorFor } from "./cloudCostTaxonomy";
import { useCloudCostProjection, useCloudCostTrends } from "./useCloudCost";

interface Props {
  variant: "past" | "future";
  // Accepts Date (Summary view) or ISO string (Jobs view); dayjs normalizes both.
  startDate: Date | string;
  endDate: Date | string | null;
  granularity?: "day" | "week" | "month";
  workspaceId?: string;
}

// Stacked-bar chart of cloud infrastructure cost by high-level line item
// (Compute / Storage / Networking / Database / Security / Analytics). The
// "past" variant shows actuals for the selected range; the "future" variant
// stacks elapsed-actual + lighter projected bars across the calendar year,
// matching the Databricks DBU future chart's actual/estimate pairing.
const DatabricksCloudCostChart = ({
  variant,
  startDate,
  endDate,
  granularity = "day",
  workspaceId,
}: Props) => {
  const { currency } = useAppState();
  const isFuture = variant === "future";

  const startStr = dayjs(startDate).format("YYYY-MM-DD");
  const endStr = endDate ? dayjs(endDate).format("YYYY-MM-DD") : "";

  const pastQuery = useCloudCostTrends(
    { start_date: startStr, end_date: endStr, granularity, workspace_id: workspaceId },
    !isFuture
  );
  const futureQuery = useCloudCostProjection({ workspace_id: workspaceId }, isFuture);

  const isLoading = futureQuery.isLoading || pastQuery.isLoading;
  const error = futureQuery.error || pastQuery.error;
  const data = useMemo<Record<string, unknown>[]>(
    () => ((isFuture ? futureQuery.data : pastQuery.data) ?? []) as Record<string, unknown>[],
    [isFuture, futureQuery.data, pastQuery.data]
  );

  const tooltipFields = useMemo(
    () => [
      {
        key: { current: isFuture ? "total" : CLOUD_TOTAL_KEY, estimate: "total_estimate" },
        label: "Total Cost",
        isTotal: true,
        hideSticker: true,
        showChangePercentage: true,
      },
      ...CLOUD_COST_CATEGORIES.map(category => ({
        key: { current: category, estimate: `${category}_estimate` },
        label: category,
        showChangePercentage: true,
      })),
    ],
    [isFuture]
  );

  if (error) {
    return (
      <FailureComponent title="Failed to load cloud cost breakdown. Please try again later." />
    );
  }
  if (isLoading) {
    return <DatabricksCostChartSkeleton />;
  }
  if (data.length === 0) {
    return (
      <Stack
        align="center"
        justify="center"
        className="al-h-[450px] al-w-full al-text-muted-foreground"
      >
        No cloud cost data available for the selected period
      </Stack>
    );
  }

  return (
    <div className="al-w-full al-h-[450px] al-p-4">
      <Chart
        {...SUMMARY_TIME_SERIES_CHART_PROPS}
        title={
          isFuture
            ? "Cloud Infrastructure Cost Projections"
            : "Cloud Infrastructure Costs by Category"
        }
        xAxisDataKey="period"
        yAxisLabel={{ value: "Cost" }}
        xAxisLabelFormatter={(value: string) =>
          isFuture || granularity === "month"
            ? dayjs(value).format("MMM YYYY")
            : dayjs(value).format("MMM D")
        }
        yAxisLabelFormatter={(value: string) =>
          formatNumber(Number(value), { currency, notation: "compact" })
        }
        type={ChartTypes.BarChart}
        data={data}
        tooltipProps={{
          content: (props: TooltipProps<number, string>) => (
            <CostChartTooltip
              {...props}
              timeRange={isFuture ? "month" : granularity}
              dateKey="period"
              isFuture={isFuture}
              fields={tooltipFields}
              colorFor={cloudCostColorFor}
              // Render category labels verbatim — "Analytics & Streaming" must
              // not be mangled by the default Snowflake title-caser.
              formatFieldLabel={label => label}
            />
          ),
        }}
      >
        {({ barSize, selectedLegend }) => (
          <>
            {CLOUD_COST_CATEGORIES.map((category, index) => {
              const isLast = index === CLOUD_COST_CATEGORIES.length - 1;
              const color = cloudCostColorFor(category);
              if (!isFuture) {
                return (
                  <Bar
                    key={category}
                    barSize={barSize}
                    name={category}
                    dataKey={category}
                    stackId="cloud"
                    fill={color}
                    hide={Boolean(selectedLegend && selectedLegend !== category)}
                    radius={isLast ? [4, 4, 0, 0] : [0, 0, 0, 0]}
                  />
                );
              }
              // Legend click passes the bar's dataKey; match both the actual and
              // projected dataKeys so clicking either reveals both bars.
              const matchesLegend =
                !selectedLegend ||
                selectedLegend === category ||
                selectedLegend === `${category}_estimate`;
              return (
                <Fragment key={category}>
                  <Bar
                    barSize={barSize}
                    name={category}
                    dataKey={category}
                    stackId="cloud"
                    fill={color}
                    hide={!matchesLegend}
                  />
                  <Bar
                    barSize={barSize}
                    name={`${category} (projected)`}
                    dataKey={`${category}_estimate`}
                    stackId="cloud"
                    fill={color}
                    fillOpacity={ESTIMATE_OPACITY}
                    hide={!matchesLegend}
                    radius={isLast ? [4, 4, 0, 0] : [0, 0, 0, 0]}
                  />
                </Fragment>
              );
            })}
          </>
        )}
      </Chart>
    </div>
  );
};

export default DatabricksCloudCostChart;
